import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Timer, Lightbulb } from "lucide-react"
import type { GameQuestion, PlayerPowerUp } from '@/types/games'

interface WordPuzzleProps {
  gameId: string
  questions: GameQuestion[]
  powerUps?: PlayerPowerUp[]
  onComplete: (score: number, time: number) => void
  onUsePowerUp?: (powerUpId: string) => Promise<boolean>
}

export function WordPuzzle({
  questions,
  onComplete,
  gameId: _gameId,
  powerUps: _powerUps,
  onUsePowerUp: _onUsePowerUp
}: WordPuzzleProps) {
  const [answer, setAnswer] = useState('')
  const [timeLeft, setTimeLeft] = useState(180)
  const [totalScore, setTotalScore] = useState(0)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [hintsUsed, setHintsUsed] = useState(0)
  const [usedLetters, setUsedLetters] = useState<number[]>([])
  
  // Get the scrambled word and correct answer
  const question = questions[currentQuestion]
  const scrambledWord = question?.answers[0] as string
  const correctAnswer = question?.answers[1] as string // Store unscrambled word as second answer
  const hints = (question as any)?.word_hints || []

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(timer)
          checkAnswer()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const handleLetterClick = (letter: string, index: number) => {
    if (!usedLetters.includes(index)) {
      setAnswer(prev => prev + letter)
      setUsedLetters(prev => [...prev, index])
    }
  }

  const handleBackspace = () => {
    if (answer.length > 0 && usedLetters.length > 0) {
      setAnswer(prev => prev.slice(0, -1))
      setUsedLetters(prev => prev.slice(0, -1))
    }
  }

  const handleClear = () => {
    setAnswer('')
    setUsedLetters([])
  }

  const moveToNextQuestion = (score: number) => {
    setTotalScore(prev => prev + score)
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
      setAnswer('')
      setUsedLetters([])
      setHintsUsed(0)
      setTimeLeft(180)
    } else {
      // Game complete
      onComplete(totalScore + score, 180 - timeLeft)
    }
  }

  const showHint = () => {
    if (hintsUsed < hints.length) {
      setHintsUsed(prev => prev + 1)
    }
  }

  const checkAnswer = () => {
    if (!answer || !correctAnswer) return
    
    // Clean up strings for comparison - keep spaces but remove other special chars
    const cleanAnswer = answer.toLowerCase().replace(/[^a-z\s]/g, '')
    const cleanCorrect = correctAnswer.toLowerCase().replace(/[^a-z\s]/g, '')
    
    const isCorrect = cleanAnswer === cleanCorrect
    
    let finalScore = 0
    if (isCorrect) {
      // Base score
      finalScore = question.base_score || 1000

      // Time bonus: Up to 50% bonus for fast completion
      const timeBonus = Math.floor((timeLeft / 180) * (finalScore * 0.5))
      finalScore += timeBonus

      // Hint penalty: -20% per hint used
      const hintPenalty = Math.floor(finalScore * (hintsUsed * 0.2))
      finalScore -= hintPenalty
    }
    
    moveToNextQuestion(finalScore)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Word Puzzle</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              <span className={timeLeft <= 30 ? "text-destructive" : ""}>
                {timeLeft}s
              </span>
            </div>
            <span className="text-lg font-normal space-x-2">
              <span>Score: {totalScore}</span>
              <span>•</span>
              <span>Question {currentQuestion + 1}/{questions.length}</span>
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-lg font-medium">
          {question.question}
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          {scrambledWord?.split('').map((letter, index) => (
            <Button
              key={index}
              variant={usedLetters.includes(index) ? "secondary" : "outline"}
              className="w-12 h-12 text-xl"
              onClick={() => handleLetterClick(letter, index)}
              disabled={usedLetters.includes(index)}
            >
              {letter}
            </Button>
          ))}
        </div>

        <div className="flex flex-col items-center gap-4">
          <div className="text-2xl font-mono tracking-widest bg-muted px-4 py-2 rounded-lg min-w-[200px] text-center">
            {answer || (scrambledWord ? '_'.repeat(scrambledWord.length) : '')}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBackspace}
              disabled={answer.length === 0}
            >
              Backspace
            </Button>
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={answer.length === 0}
            >
              Clear
            </Button>
          </div>
        </div>

        {hints.length > 0 && (
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={showHint}
              disabled={hintsUsed >= hints.length}
            >
              <Lightbulb className="h-4 w-4 mr-2" />
              Show Hint ({hints.length - hintsUsed} remaining)
            </Button>
            {hints.slice(0, hintsUsed).map((hint: string, index: number) => (
              <p key={index} className="text-sm text-muted-foreground">
                Hint {index + 1}: {hint}
              </p>
            ))}
          </div>
        )}

        <Button
          className="w-full"
          onClick={checkAnswer}
          disabled={!scrambledWord || answer.length !== scrambledWord.length}
        >
          Submit Answer
        </Button>
      </CardContent>
    </Card>
  )
}